/**
 * The bank account country for a Sales Partner's NEW Stripe Express
 * account (item 6, 2026-10-03). Warren: "Sales Partner Connect accounts:
 * explicit US or Canada choice, never defaulting to Canada, same pattern
 * as host accounts."
 *
 * POST /admin/agents/:id/connect/start refuses to create an account
 * without a country (400 CONNECT_COUNTRY_REQUIRED) and ignores one for an
 * existing account. These rules keep every start surface (the Agents
 * edit modal and the Sales Partner Connect banner) on the host pattern:
 * a select that starts empty, the cannot-change warning, a confirmation
 * that resets when the country changes, and a body that carries the
 * country only when no account exists.
 *
 * The host pattern's two stops come with it (the driver app's
 * apiTakesCountry and createdInOtherCountry). An Api from before this
 * change ignores the country and creates a Canadian account Stripe can
 * never move, so no account is created until the agent row shows the
 * new Api (agentApiTakesCountry), and after a start that sent a country
 * the reply's country is checked before Stripe opens
 * (agentStartCountryProblem).
 *
 * The copy lives in payoutSetupCopy.ts. Each copy function takes an
 * optional trailing locale (payoutLocale() on the screen) and speaks
 * English when it is left out.
 */
import { fillPayoutCopy, payoutCountryInText, payoutCountryName, payoutSetupCopy } from './payoutSetupCopy';

const EN = payoutSetupCopy('en');

export const AGENT_PAYOUT_COUNTRIES = ['US', 'CA'] as const;
export type AgentPayoutCountry = typeof AGENT_PAYOUT_COUNTRIES[number];

/** The field's state. '' is "not chosen yet", the starting value. */
export interface AgentPayoutChoice {
  country: AgentPayoutCountry | '';
  confirmed: boolean;
}

export const EMPTY_AGENT_PAYOUT_CHOICE: AgentPayoutChoice = { country: '', confirmed: false };

export function isAgentPayoutCountry(value: unknown): value is AgentPayoutCountry {
  return value === 'US' || value === 'CA';
}

/** A new select value. Any change clears the confirmation, so a tick
 *  given for one country never carries over to the other; anything but
 *  US or CA reads as not chosen. */
export function chooseAgentPayoutCountry(value: string): AgentPayoutChoice {
  return { country: isAgentPayoutCountry(value) ? value : '', confirmed: false };
}

/** The confirmation can only be ticked once a country is chosen. */
export function confirmAgentPayoutCountry(choice: AgentPayoutChoice, confirmed: boolean): AgentPayoutChoice {
  return { country: choice.country, confirmed: choice.country !== '' && confirmed };
}

/**
 * True when the agent row came from an Api that creates the account in
 * the chosen country. Since 2026-10-03 every agent row carries
 * `payout_country` (null before an account exists); an older Api omits
 * it, and its /connect/start ignores the country and creates a Canadian
 * account. So no create is offered until the row says the Api is the new
 * one (deploy the Api first).
 */
export function agentApiTakesCountry(agent: object | null | undefined): boolean {
  return !!agent && 'payout_country' in agent;
}

/** Why the Stripe button waits while agentApiTakesCountry is false. */
export const AGENT_PAYOUT_UPDATING = EN.agentUpdating;

/** Whether the Stripe button may be pressed. An existing account resumes
 *  with no country; a new one needs a chosen and confirmed country and an
 *  Api that honours it (agentApiTakesCountry). */
export function canStartAgentConnect(
  hasAccount: boolean,
  choice: AgentPayoutChoice,
  apiTakesCountry: boolean,
): boolean {
  if (hasAccount) return true;
  return apiTakesCountry && choice.country !== '' && choice.confirmed;
}

/** The start request body: the country only when no account exists (the
 *  route ignores it otherwise), and nothing at all for a resume. */
export function agentConnectStartBody(
  hasAccount: boolean,
  choice: AgentPayoutChoice,
): { country: AgentPayoutCountry } | undefined {
  if (hasAccount || choice.country === '') return undefined;
  return { country: choice.country };
}

/** The select option name. */
export function agentPayoutCountryName(country: AgentPayoutCountry): string {
  return payoutCountryName(EN, country);
}

/** The country inside a sentence ("is in the United States"). */
export function agentPayoutCountryInText(country: AgentPayoutCountry): string {
  return payoutCountryInText(EN, country);
}

/** A stored country for display: the name for US and CA, a legacy code
 *  (GB, AU) as stored. */
export function agentPayoutCountryDisplay(code: string): string {
  return isAgentPayoutCountry(code) ? agentPayoutCountryName(code) : code;
}

/**
 * After a start that sent `sent`, what is wrong with the account the
 * reply's link is for, or null when Stripe may open. Nothing sent (a
 * resume) is never a problem. A reply with no country is 'unconfirmed':
 * an older Api replies { url } alone because it ignored the choice, so
 * the account may be in the other country. A different country is
 * 'other': a lost race or a stale screen pointed at an existing account.
 */
export type AgentStartCountryProblem = { kind: 'unconfirmed' } | { kind: 'other'; country: string };

export function agentStartCountryProblem(
  sent: AgentPayoutCountry | null | undefined,
  returned: unknown,
): AgentStartCountryProblem | null {
  if (!sent) return null;
  if (typeof returned !== 'string' || returned === '') return { kind: 'unconfirmed' };
  return returned === sent ? null : { kind: 'other', country: returned };
}

/** The message shown instead of opening Stripe. */
export function agentStartCountryStopMessage(
  subject: AgentPayoutSubject,
  chosen: AgentPayoutCountry,
  problem: AgentStartCountryProblem,
  locale: unknown = 'en',
): string {
  const text = payoutSetupCopy(locale);
  if (problem.kind === 'unconfirmed') {
    return subject === 'self' ? text.agentStopUnconfirmedSelf : text.agentStopUnconfirmedPartner;
  }
  const actual = isAgentPayoutCountry(problem.country)
    ? payoutCountryInText(text, problem.country)
    : problem.country;
  return fillPayoutCopy(subject === 'self' ? text.agentStopOtherSelf : text.agentStopOtherPartner,
    { account: actual, country: payoutCountryInText(text, chosen) });
}

/**
 * The stored country of an EXISTING payout account, for display. Null
 * without an account: Agent.country is then the schema default, not a
 * choice, and must not be shown. A legacy code outside US and CA (GB, AU)
 * is shown as stored.
 */
export function agentPayoutAccountCountryLabel(
  agent: { stripe_connect_account_id: string | null; country?: string | null },
): string | null {
  if (!agent.stripe_connect_account_id) return null;
  const stored = agent.country ?? null;
  if (!stored) return null;
  return agentPayoutCountryDisplay(stored);
}

/** Who the bank account belongs to, for the copy: the Sales Partner on
 *  their own banner ('self'), or an admin acting for them ('partner'). */
export type AgentPayoutSubject = 'self' | 'partner';

export function agentPayoutWarning(subject: AgentPayoutSubject, locale: unknown = 'en'): string {
  const text = payoutSetupCopy(locale);
  return subject === 'self' ? text.agentWarningSelf : text.agentWarningPartner;
}

export function agentPayoutConfirmLabel(subject: AgentPayoutSubject, country: AgentPayoutCountry, locale: unknown = 'en'): string {
  const text = payoutSetupCopy(locale);
  return fillPayoutCopy(subject === 'self' ? text.agentConfirmSelf : text.agentConfirmPartner,
    { country: payoutCountryInText(text, country) });
}
